import { Building2, Check, Home, Mail, Users } from "lucide-react";
import type { ReactNode } from "react";
import { PROFILE_OPTIONS } from "../../data";
import type { OnboardingFlowData } from "../../hooks/use-onboarding-flow";
import { formatOnboardingAddress } from "../../utils/format";
import { StepShell } from "../step-shell";

interface SummaryStepProps {
	data: OnboardingFlowData;
	submitting?: boolean;
	error?: string;
	onBack: () => void;
	onFinish: () => void;
}

function SummaryRow({
	icon,
	label,
	children,
}: {
	icon: ReactNode;
	label: string;
	children: ReactNode;
}) {
	return (
		<div className="summary-row">
			<span className="summary-row__icon">{icon}</span>
			<div className="summary-row__body">
				<p className="field-label">{label}</p>
				<div className="summary-row__value">{children}</div>
			</div>
		</div>
	);
}

export function SummaryStep({
	data,
	submitting = false,
	error,
	onBack,
	onFinish,
}: SummaryStepProps) {
	const isSingle = data.mode === "single-project";
	const obras = isSingle && data.singleProject ? [data.singleProject] : [];

	const profileOption = PROFILE_OPTIONS.find(
		(option) => option.id === data.profile?.type,
	);
	const ProfileIcon = profileOption?.icon ?? Building2;

	return (
		<StepShell
			title="Tudo pronto para começar"
			description="Confira o resumo do cadastro antes de entrar no painel."
			onBack={onBack}
			onContinue={onFinish}
			continueDisabled={submitting}
			continueLabel={
				submitting
					? "Enviando…"
					: error
						? "Tentar novamente"
						: "Entrar no painel"
			}
		>
			{!!error && (
				<p role="alert" className="form-error-alert">
					{error}
				</p>
			)}

			<div className="summary-banner">
				<span className="summary-banner__icon">
					<Check />
				</span>
				<div>
					<p className="summary-banner__title">
						{data.profile?.companyName
							? `${data.profile.companyName} está pronta!`
							: "Cadastro concluído!"}
					</p>
					<p className="summary-banner__subtitle">
						{isSingle
							? "1 obra cadastrada."
							: "Seu canteiro está pronto para receber obras."}
					</p>
				</div>
			</div>

			<SummaryRow icon={<Mail />} label="Conta">
				{data.account?.email ?? "Conta conectada via Google"}
			</SummaryRow>

			<SummaryRow icon={<ProfileIcon />} label="Perfil">
				{profileOption?.title ?? "—"}
				{data.profile?.companyName ? (
					<span className="summary-row__muted">
						{" · "}
						{data.profile.companyName}
					</span>
				) : null}
			</SummaryRow>

			<SummaryRow
				icon={isSingle ? <Home /> : <Building2 />}
				label={isSingle ? "Obra única" : "Canteiro"}
			>
				{isSingle ? (
					<>
						<p>
							{data.singleProject
								? formatOnboardingAddress(data.singleProject.address)
								: "—"}
						</p>
						<p className="summary-row__note">
							Canteiro criado automaticamente com o endereço da obra.
						</p>
					</>
				) : (
					<>
						<p className="summary-row__strong">{data.site?.name ?? "—"}</p>
						<p className="summary-row__note">
							{data.site ? formatOnboardingAddress(data.site.address) : "—"}
						</p>
					</>
				)}
			</SummaryRow>

			{obras.length > 0 && (
				<div className="summary-obras">
					<p className="field-label">Obra</p>
					<ul className="summary-obras__list">
						{obras.map((obra) => (
							<li key={obra.id} className="summary-obras__item">
								<span
									className="summary-obras__color"
									style={{ backgroundColor: obra.color }}
								/>
								<span className="summary-obras__name">{obra.name}</span>
								<span className="summary-obras__type">{obra.type}</span>
							</li>
						))}
					</ul>
				</div>
			)}

			<SummaryRow icon={<Users />} label="Equipe">
				{data.invites.length > 0
					? `${data.invites.length} convite(s) a enviar`
					: "Nenhum convite — você pode convidar depois."}
			</SummaryRow>
		</StepShell>
	);
}
